import { checkProductPermission } from './permissionUtils'
import { checkPermission } from '../../../common/utils/helpers'

/**
 * Determines which products should be shown for a product select dropdown
 * @param {Object} params - Product filtering parameters
 * @param {Array} params.products - All available products
 * @param {Array} params.selected_products - Currently selected products
 * @param {boolean} params.editing - Whether in editing mode
 * @param {boolean} params.is_follow_up - Whether this is a follow-up order
 * @param {Object} params.productPermissions - Product permissions object
 * @param {Object} params.role - User role object
 * @param {number} params.selectIndex - Index of the product select dropdown
 * @returns {Array} Filtered array of products that can be selected
 */
export const determineProductOptions = ({
  products,
  selected_products,
  editing,
  is_follow_up,
  productPermissions,
  role,
  selectIndex,
}) => {
  // determine which products should be shown for the select at selectIndex
  const allProducts = products
  const selectedProducts = selected_products

  // normally, the 0th item allows the user to change job type
  let canSelectDifferentJobType = selectIndex === 0
  let canSelectReviewProducts = true
  let jobTypeToMatch
  if (selectedProducts && selectedProducts[0] && selectedProducts[0].job_type) {
    jobTypeToMatch = selectedProducts[0].job_type
  }

  if (editing) {
    // we need special logic if the user is editing an order
    if (jobTypeToMatch === 'review') {
      // cannot change away from review
      canSelectDifferentJobType = false
    } else {
      // make sure they don't change it to a review order
      canSelectReviewProducts = false
    }
  } else if (is_follow_up) {
    // follow up orders cannot use review products
    canSelectReviewProducts = false
  }

  const userHasReviewOrderPermission = checkPermission('order_create_review')
  if (!userHasReviewOrderPermission) {
    // user doesn't even have the permission to view review products
    canSelectReviewProducts = false
  }

  return allProducts.filter((product) => {
    const hasPermission = checkProductPermission({
      is_follow_up,
      productPermissions,
      role,
      productId: product.id,
    })
    if (!hasPermission) {
      return false
    }

    const productJobType = product.job_type
    if (!canSelectReviewProducts && productJobType === 'review') {
      // they cannot access review products for this order
      return false
    }

    if (!canSelectDifferentJobType && jobTypeToMatch !== productJobType) {
      // they should not be allowed to select a different job type right now
      return false
    }

    // we passed the checks, so this product is fine
    return true
  })
}

/**
 * Checks if any of the selected products have appraiser overrides
 * @param {Array} selected_products - Array of selected products
 * @returns {boolean} True if any product has appraiser overrides
 */
export const checkForAppraiserOverrides = (selected_products) => {
  let appraiserOverridesExist = false

  selected_products.forEach((product) => {
    if (product && (product.appraiser_overrides || product.has_appraiser_overrides)) {
      appraiserOverridesExist = true
    }
  })
  return appraiserOverridesExist
}
